/**
 * dealActions.ts — Accept, Decline and Withdraw, against the real endpoints.
 *
 * Blake's three controls announce an outcome and make no server call. His
 * accept is the worst of that class: it sets `t.fees` with a hardcoded
 * barterTax of 12 and shipping of 14, and a truthy `t.fees` is ALSO the only
 * thing that makes `#/checkout/<id>` resolvable (app.js route()) — so one tap
 * produced an acceptance that never happened, priced with invented numbers,
 * and a checkout screen to spend them on. This module does the write instead.
 *
 * 🔒 ALL THREE SELECTORS STAY IN GUARDED_WRITES, and that is a fail-safe rather
 * than an oversight. installWriteGuard registers a capture listener on the same
 * document and calls `event.stopPropagation()`, never stopImmediatePropagation
 * (writeGuard.ts) — so a second capture listener registered afterwards still
 * receives the click. Both run: the guard kills his fabricator on the way down,
 * this module does the real PATCH. That ordering is the fail-safe, exactly as
 * argued for [data-vote] and [data-follow]: if anything here fails to install
 * or throws, the buttons go back to being merely DEAD rather than to claiming
 * an acceptance the server never recorded. So this module does NOT stop
 * propagation itself, and must never read `event.defaultPrevented` — the guard
 * calls preventDefault() before we run.
 *
 * 🪤 HIS BUTTONS CARRY THE THREAD ID; THE ENDPOINTS TAKE AN OFFER ID. `t.id` is
 * `d:<counterpartId>:<listingId>` — one Blake thread per offer CHAIN — while
 * PATCH /api/offers/:id needs that chain's head offer. `__gbzThreadOfferId`
 * (floorTransport.ts) answers it, reading back the id the DETAIL pass stashed
 * on the row, scoped to this chain's listing. A row whose detail has not loaded
 * resolves to null, and this REFUSES OUT LOUD rather than guessing: the same
 * rule followUser applies to a handle with no id, for the same reason — a
 * button that silently does nothing is the defect being fixed here.
 *
 * ✅ The hazard that was measured and is NOT present: a server person row can
 * carry several deal chains, but mapDealThread fans it out to one Blake thread
 * per chain, so the id under the button is the chain the visitor clicked and
 * never a sibling's. Measured 2026-08-29 against his real renderer with two
 * live chains; an UNSCOPED resolve returns the wrong chain's offer, which is
 * exactly what the `d:` key prevents.
 *
 * 🔴 NOTHING IS PAINTED FROM A GUESS. All three endpoints answer a flat
 * `{ ok: true }`, and the words this surface renders (status.word, cta,
 * stepper) are computed by lib/dealState on the SERVER, which support cannot
 * import. So a successful write is followed by a re-read of the list route and
 * the row repaints from the server's own state machine. Deriving "it must say
 * PAY now" locally would be the same fabrication with better manners.
 */

import { installDealThread, refreshDeals } from "./floorTransport";

export const ACCEPT_SELECTOR = "[data-acceptoffer]";
export const DECLINE_SELECTOR = "[data-declineoffer]";
/** 🪤 THE OFFER WITHDRAW, NOT THE WALLET ONE. The money withdrawal is
 *  `#wal-withdraw-go`, a different id on a different screen — writeGuard.ts
 *  documents the same name collision at length. */
export const WITHDRAW_SELECTOR = "[data-withdraw]";

/** Only for a failure the server gave no sentence for. */
export const ACCEPT_FAILURE_MESSAGE = "Couldn't accept that offer — please try again";
/** Only for a failure the server gave no sentence for. */
export const DECLINE_FAILURE_MESSAGE = "Couldn't decline that offer — please try again";
/** Only for a failure the server gave no sentence for. */
export const WITHDRAW_FAILURE_MESSAGE = "Couldn't withdraw that offer — please try again";

export const SIGNIN_MESSAGE = "Sign in to respond to an offer";

/** The row carries no offer id yet — see the header. Never a silent no-op. */
export const UNRESOLVED_OFFER_MESSAGE =
  "Open the deal first — we can't tell which offer this is yet";

/* The three success lines. Each is Blake's own sentence with the clause we
   cannot stand behind removed, rather than a new one written here:

     his "Accepted · both sides head to escrow checkout" — the escrow half
       describes a screen this lane has not wired; the repaint says the rest.
     his "Declined · @X was notified" — the server does push and email the
       proposer, but fire-and-forget after the response, so the notification is
       not ours to assert.
     his "Offer withdrawn · @X was notified" — 🔴 FALSE EVEN NOW. PATCH
       /api/offers/:id/cancel dispatches NOTHING: no push, no email, no in-app
       row, no conversation bump. The counterparty is told nothing by the
       server, so neither is the person withdrawing. */
export const ACCEPTED_MESSAGE = "Offer accepted";
export const DECLINED_MESSAGE = "Offer declined";
export const WITHDRAWN_MESSAGE = "Offer withdrawn";

/** Blake's own arm label (app.js: `el.textContent = "Tap again to withdraw"`),
 *  said out loud because the guard stops his arming at stage one. */
export const WITHDRAW_ARM_MESSAGE = "Tap again to withdraw this offer";

/** His own arm window, matched so the two time out identically (app.js: 2600). */
export const WITHDRAW_ARM_WINDOW_MS = 2600;

type OfferAction = "accept" | "decline" | "withdraw";

/* His word is "withdraw"; the wire's word is "cancel". They are not the same,
   and the wrong one is a 404 rather than a visible failure — the same kind of
   translation listingVotes makes for fair/over -> up/down.

   🪤 NOT "back-out". POST /api/offers/:id/back-out is the POST-acceptance
   reversal: it refunds a Stripe escrow split, restores fee credit and reverses
   barter tax. It is money-touching and out of scope. */
const ENDPOINT: Readonly<Record<OfferAction, string>> = {
  accept: "accept",
  decline: "decline",
  withdraw: "cancel",
};

const FAILURE_MESSAGE: Readonly<Record<OfferAction, string>> = {
  accept: ACCEPT_FAILURE_MESSAGE,
  decline: DECLINE_FAILURE_MESSAGE,
  withdraw: WITHDRAW_FAILURE_MESSAGE,
};

const SUCCESS_MESSAGE: Readonly<Record<OfferAction, string>> = {
  accept: ACCEPTED_MESSAGE,
  decline: DECLINED_MESSAGE,
  withdraw: WITHDRAWN_MESSAGE,
};

interface DealActionsWindow extends Window {
  toast?: (message: string) => void;
  __gbzThreadOfferId?: (threadId: string) => string | null;
  __gbzSelectDealByOffer?: (offerId: string) => boolean;
}

/**
 * Wires Accept / Decline / Withdraw to
 * PATCH /api/offers/:id/{accept,decline,cancel}.
 *
 * Returns a teardown that removes the listener, which the tests need because
 * they share one jsdom window across cases.
 */
export function installDealActions(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
  {
    notify = (message: string) => {
      // #toast only exists after app.js's first render; a click that somehow
      // arrives before it degrades to silence rather than throwing inside a
      // capture listener. Same lifecycle guard as the rest of the lane.
      if (!win.document.getElementById("toast")) return;
      (win as DealActionsWindow).toast?.(message);
    },
    now = () => Date.now(),
  }: { notify?: (m: string) => void; now?: () => number } = {},
): () => void {
  /* In flight, per thread. His handlers carried no re-entrancy guard because
     they were synchronous; with a real await behind the button a double-tap is
     two PATCHes racing, and the second lands on a row the first already moved —
     which the server answers "Offer is no longer pending", making a successful
     accept read as a failed one. */
  const busy = new Set<string>();

  /* 🪤 THE WITHDRAW CONFIRM IS OURS NOW. His is a two-stage arm — first tap
     sets el.dataset.armed and rewrites the label, second tap withdraws — and
     the guard stops that at stage one, so the control never arms at all.
     Keeping a confirm step is deliberate: a withdrawal is visible to the
     counterparty and tapping again does not undo it. Held in module state
     rather than on the element because his render() replaces #app wholesale,
     taking any dataset flag with it. */
  const armedAt = new Map<string, number>();

  function hitFor(target: Element): { action: OfferAction; threadId: string } | null {
    const accept = target.closest(ACCEPT_SELECTOR) as HTMLElement | null;
    if (accept) {
      const threadId = accept.dataset.acceptoffer;
      return threadId ? { action: "accept", threadId } : null;
    }
    const decline = target.closest(DECLINE_SELECTOR) as HTMLElement | null;
    if (decline) {
      const threadId = decline.dataset.declineoffer;
      return threadId ? { action: "decline", threadId } : null;
    }
    const withdraw = target.closest(WITHDRAW_SELECTOR) as HTMLElement | null;
    if (withdraw) {
      const threadId = withdraw.dataset.withdraw;
      return threadId ? { action: "withdraw", threadId } : null;
    }
    return null;
  }

  async function run(action: OfferAction, offerId: string, threadId: string): Promise<void> {
    const res = await authFetch(`/api/offers/${encodeURIComponent(offerId)}/${ENDPOINT[action]}`, {
      method: "PATCH",
    });

    if (res.status === 401) {
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      /* The server's own words when it gave any, ours only when it did not.
         These are sentences the visitor needs to read rather than a flattened
         failure: 400 "Offer is no longer pending" means the counterparty moved
         first, 400 "Add a shipping address to your profile before accepting
         offers." names the exact thing standing in the way, and 403 "Not your
         offer to accept" means the tap was never going to work.

         🪤 PATCH /cancel can also fail with a NON-JSON body — reverseItemToActive
         throws hard and express answers with its default error page — so the
         parse must not be allowed to throw inside a capture listener. */
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      notify(body.error ?? FAILURE_MESSAGE[action]);
      return;
    }

    /* 🔑 THE DEAL YOU ACTED ON IS THE DEAL YOU HAVE OPEN (card 1219600788020719).
       A person is one row, which names their newest deal, so acting on any
       other deal of theirs used to move that deal to another tab while the
       selection stayed on the newest: the acted-on deal left the pane. Selected
       here, after the server accepted the write (a refusal returned above and
       moved nothing) and before the list re-read, so that read's tab-follow
       (floorTransport.ts __gbzRefreshDeals) carries the page to where this deal
       went. The bridge finds the row by the offer id resolved from this same
       row, and off All it sets the tab to the row's own: the painted one when
       the control was in the pane, the deal's tab when it was in the All Offers
       feed. All stays. */
    (win as DealActionsWindow).__gbzSelectDealByOffer?.(offerId);

    /* Re-read before saying anything, and both reads are best-effort by design:
       the write has already landed on the server, so a failure here costs a
       repaint and not the action. Reporting "couldn't accept" because a GET
       failed would be its own false claim.

       The LIST first, because it owns the words on the row and because accept
       retires every competing offer on that listing — rows the visitor may be
       looking at right now. Then this one thread's DETAIL, because accept
       writes two system messages into the conversation and the open thread
       would otherwise show a history that stops just before the thing that was
       clicked. */
    await refreshDeals(win, authFetch, myUserId).catch(() => null);
    await installDealThread(win, authFetch, threadId, myUserId).catch(() => false);

    // AFTER the repaint, never before it: saying it while the row still reads
    // NEW OFFER would put the claim ahead of the evidence for it.
    notify(SUCCESS_MESSAGE[action]);
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const hit = hitFor(target);
    if (!hit) return;
    const { action, threadId } = hit;

    /* 🔴 REFUSE OUT LOUD. The offer id is stashed by the thread-detail pass, so
       a row nobody has opened resolves to nothing. Measured: it resolves to
       null rather than to a sibling chain's offer, so the failure is safe — but
       silence here would rebuild the exact defect this module removes. */
    const offerId = (win as DealActionsWindow).__gbzThreadOfferId?.(threadId) ?? null;
    if (!offerId) {
      notify(UNRESOLVED_OFFER_MESSAGE);
      return;
    }

    if (busy.has(threadId)) return;

    if (action === "withdraw") {
      const armed = armedAt.get(threadId);
      if (armed === undefined || now() - armed > WITHDRAW_ARM_WINDOW_MS) {
        armedAt.set(threadId, now());
        notify(WITHDRAW_ARM_MESSAGE);
        return;
      }
      armedAt.delete(threadId);
    }

    busy.add(threadId);
    void run(action, offerId, threadId)
      .catch(() => notify(FAILURE_MESSAGE[action]))
      .finally(() => busy.delete(threadId));
  };

  /* Capture on the document, so this sees the click before his target-phase
     listener — and deliberately NOT stopping propagation, so the guard still
     fires and stays the fail-safe. See the header for why all three selectors
     stay in GUARDED_WRITES. */
  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
